import { QUIZ_QUESTIONS, getQuizQuestionById, type QuizQuestion } from '../catalog/quizQuestions';
import { isoDateFromMs } from '../time/moscowCalendar';
import {
  BUSINESS_TASK_PASS_REWARD,
  NOTIFICATIONS_PASS_REWARD,
  PASSES_CAP,
  QUIZ_DAILY_LIMIT,
} from './constants';
import { getNextDailyLoginReward, isDailyLoginRewardClaimedToday, isDailyLoginStreakCompleted } from './dailyLogin';
import type { PlayerEconomyState } from './types';
import {
  isNotificationsRewardClaimed,
  isVkSubscribeRewardClaimed,
} from './vkTasks';

export const QUIZ_PASS_REWARD = 1 as const;

export type QuizAnswerResult = {
  correct: boolean;
  state: PlayerEconomyState;
  rewardPasses: number;
};

export type FlightTicketTaskKind =
  | 'daily_login'
  | 'quiz'
  | 'notifications'
  | 'vk_subscribe'
  | 'invite_friend';

export type FlightTicketTask = {
  id: string;
  kind: FlightTicketTaskKind;
  titleKey: string;
  rewardPasses: number;
  completed: boolean;
  actionEnabled: boolean;
  progressText: string;
};

function answeredQuizIds(state: PlayerEconomyState): readonly string[] {
  return state.answeredQuizQuestionIds ?? [];
}

export function getQuizProgress(state: PlayerEconomyState): { answered: number; total: number } {
  const answered = answeredQuizIds(state).length;
  return { answered, total: QUIZ_QUESTIONS.length };
}

export function isQuizComplete(state: PlayerEconomyState): boolean {
  const { answered, total } = getQuizProgress(state);
  return total > 0 && answered >= total;
}

export function getNextQuizQuestion(state: PlayerEconomyState): QuizQuestion | null {
  const done = new Set(answeredQuizIds(state));
  return QUIZ_QUESTIONS.find((q) => !done.has(q.id)) ?? null;
}

export function getQuizAnswersToday(state: PlayerEconomyState, nowMs = Date.now()): number {
  const today = isoDateFromMs(nowMs);
  if (state.quizDailyDate !== today) {
    return 0;
  }
  return Math.max(0, Math.floor(state.quizDailyAnsweredCount ?? 0));
}

export function isQuizDailyLimitReached(state: PlayerEconomyState, nowMs = Date.now()): boolean {
  return getQuizAnswersToday(state, nowMs) >= QUIZ_DAILY_LIMIT;
}

export function submitQuizAnswer(
  state: PlayerEconomyState,
  questionId: string,
  selectedIndex: number,
  nowMs = Date.now(),
): QuizAnswerResult {
  const question = getQuizQuestionById(questionId);
  if (question == null) {
    throw new Error(`submitQuizAnswer: unknown question id ${questionId}`);
  }
  if (answeredQuizIds(state).includes(questionId)) {
    return { correct: true, state, rewardPasses: 0 };
  }
  if (isQuizDailyLimitReached(state, nowMs)) {
    return { correct: false, state, rewardPasses: 0 };
  }
  if (!Number.isInteger(selectedIndex) || selectedIndex < 0 || selectedIndex >= question.options.length) {
    throw new Error('submitQuizAnswer: selectedIndex out of range');
  }

  const correct = selectedIndex === question.correctIndex;
  const answeredQuizQuestionIds = [...answeredQuizIds(state), questionId];
  const quizDailyDate = isoDateFromMs(nowMs);
  const quizDailyAnsweredCount = getQuizAnswersToday(state, nowMs) + 1;

  if (!correct) {
    return {
      correct: false,
      state: { ...state, answeredQuizQuestionIds, quizDailyDate, quizDailyAnsweredCount },
      rewardPasses: 0,
    };
  }

  const passesBalance = Math.min(PASSES_CAP, state.passesBalance + QUIZ_PASS_REWARD);
  return {
    correct: true,
    state: { ...state, answeredQuizQuestionIds, passesBalance, quizDailyDate, quizDailyAnsweredCount },
    rewardPasses: QUIZ_PASS_REWARD,
  };
}

export function buildFlightTicketTasks(state: PlayerEconomyState, nowMs = Date.now()): FlightTicketTask[] {
  const { answered, total } = getQuizProgress(state);
  const quizCompleted =
    isQuizComplete(state) || isQuizDailyLimitReached(state, nowMs);
  const dailyStreakCompleted = isDailyLoginStreakCompleted(state);
  const dailyClaimedToday = isDailyLoginRewardClaimedToday(state, nowMs);
  const dailyReward = getNextDailyLoginReward(state, nowMs);
  const dailyProgressDays = dailyStreakCompleted ? 7 : Math.min(7, state.streakDays);

  return [
    {
      id: 'daily_login',
      kind: 'daily_login',
      titleKey: 'game.flightTicket.taskDailyLogin',
      rewardPasses: dailyReward,
      completed: dailyStreakCompleted || dailyClaimedToday,
      actionEnabled: !dailyStreakCompleted && !dailyClaimedToday,
      progressText: `${dailyProgressDays}/7`,
    },
    {
      id: 'quiz',
      kind: 'quiz',
      titleKey: 'game.flightTicket.taskQuiz',
      rewardPasses: QUIZ_PASS_REWARD,
      completed: quizCompleted,
      actionEnabled: !quizCompleted,
      progressText: `${answered}/${total}`,
    },
    {
      id: 'notifications',
      kind: 'notifications',
      titleKey: 'game.flightTicket.taskNotifications',
      rewardPasses: NOTIFICATIONS_PASS_REWARD,
      completed: isNotificationsRewardClaimed(state),
      actionEnabled: !isNotificationsRewardClaimed(state),
      progressText: '',
    },
    {
      id: 'vk_subscribe',
      kind: 'vk_subscribe',
      titleKey: 'game.flightTicket.taskVkSubscribe',
      rewardPasses: BUSINESS_TASK_PASS_REWARD,
      completed: isVkSubscribeRewardClaimed(state),
      actionEnabled: !isVkSubscribeRewardClaimed(state),
      progressText: '',
    },
    {
      id: 'invite_friend',
      kind: 'invite_friend',
      titleKey: 'game.flightTicket.taskInviteFriend',
      rewardPasses: BUSINESS_TASK_PASS_REWARD,
      completed: false,
      actionEnabled: false,
      progressText: '',
    },
  ];
}
